import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PageHeader, Button, Badge } from "@/components/ui/PageHeader";
import {
  listAdminReports,
  resolveReports,
  REASON_LABELS,
  type ReportGroup,
  type ReportReason,
} from "@/api/reports";

/**
 * The moderation queue — grouped by content item, loudest first.
 *
 * The grouping is the design: five reports on one post are one problem, not
 * five, and the thing needing eyes soonest is the thing most people flagged.
 * The server sends that order; this page renders it as received.
 *
 * Resolution is per content item and covers every open report on it at once.
 * "Reviewed" means a human looked; "dismissed" means the reports were
 * unfounded. Neither deletes anything — removal is a separate, deliberate act
 * (redaction on messages, moderation on posts), so a resolve here can never
 * silently destroy content.
 */

const STATUSES = ["open", "reviewed", "dismissed"] as const;

export default function AdminReportsLab() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<(typeof STATUSES)[number]>("open");
  // The people-names list's owner reads "this is not how I'd say it" here; the moderation list never shows it.
  const [wordsOnly, setWordsOnly] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin-reports", status, wordsOnly],
    queryFn: () => listAdminReports({ status, ...(wordsOnly ? { contentType: "people_name" as const } : {}) }),
  });

  const groups = data?.data.items ?? [];

  async function resolve(
    group: ReportGroup,
    resolution: "reviewed" | "dismissed",
  ) {
    const key = `${group.contentType}:${group.contentId}`;
    setBusyKey(key);
    setError(null);
    try {
      await resolveReports({
        contentType: group.contentType,
        contentId: group.contentId,
        resolution,
      });
      qc.invalidateQueries({ queryKey: ["admin-reports"] });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Resolve failed");
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <div className="max-w-4xl">
      <PageHeader
        title="Content reports"
        subtitle="Grouped by item, most-reported first. Resolving covers every open report on the item."
        breadcrumbs={[
          { label: "Lab", href: "/" },
          { label: "Admin", href: "/admin" },
          { label: "Reports" },
        ]}
      />

      {/* ── status filter ───────────────────────────────────────── */}
      <div className="mb-5 inline-flex rounded-full border border-gray-200 bg-white p-1">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`rounded-full px-4 py-1.5 text-sm font-medium capitalize transition-colors ${
              status === s
                ? "bg-gray-900 text-white"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            {s}
          </button>
        ))}
      </div>
      {/* The people-names list's owner reads the words members questioned here; the moderation list never shows them. */}
      <label className="mb-5 ml-3 inline-flex items-center gap-2 text-sm text-gray-700">
        <input type="checkbox" checked={wordsOnly} onChange={(e) => setWordsOnly(e.target.checked)} />
        people-names only ("this is not how I'd say it")
      </label>

      {error && (
        <div className="mb-4 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}

      {!isLoading && groups.length === 0 && (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center">
          <p className="font-medium text-gray-900">
            {status === "open" ? "Nothing waiting" : `No ${status} reports`}
          </p>
          {status === "open" && (
            <p className="mt-1 text-sm text-gray-500">
              An empty open queue is the good outcome — nothing the community
              flagged is unhandled.
            </p>
          )}
        </div>
      )}

      <div className="space-y-4">
        {groups.map((g) => {
          const key = `${g.contentType}:${g.contentId}`;
          return (
            <section key={key} className="rounded-2xl border border-gray-200 bg-white p-4">
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant={g.reportCount >= 3 ? "red" : "yellow"}>
                  {g.reportCount} {g.reportCount === 1 ? "report" : "reports"}
                </Badge>
                <Badge variant="gray">{g.contentType.replace(/_/g, " ")}</Badge>
                <ContentLink type={g.contentType} id={g.contentId} />

                {status === "open" && (
                  <div className="ml-auto flex gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      isLoading={busyKey === key}
                      onClick={() => resolve(g, "reviewed")}
                    >
                      Mark reviewed
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={busyKey === key}
                      onClick={() => resolve(g, "dismissed")}
                    >
                      Dismiss
                    </Button>
                  </div>
                )}
              </div>

              {/* Why people reported it — the reviewer's actual signal. A
                  self-harm concern is surfaced above the fold regardless of
                  where it sits in the list. */}
              <ul className="mt-3 space-y-2">
                {g.reports.map((r) => (
                  <li key={r.id} className="flex flex-wrap items-baseline gap-x-2 text-sm">
                    <ReasonChip reason={r.reason} />
                    <span className="text-gray-500">
                      {r.reporter.name} · {new Date(r.createdAt).toLocaleDateString()}
                    </span>
                    {r.details && <span className="w-full text-gray-700 pl-1">“{r.details}”</span>}
                  </li>
                ))}
              </ul>
            </section>
          );
        })}
      </div>
    </div>
  );
}

/**
 * A reviewer needs the content, not a UUID. Voices and questions have Lab
 * pages to open; the three nested types (story, answer, comment) do not have
 * standalone pages, and the honest thing is to say so rather than fake a link.
 */
function ContentLink({ type, id }: { type: string; id: string }) {
  if (type === "voice")
    return (
      <Link to={`/lab/voices/${id}`} className="text-sm text-blue-700 hover:underline">
        Open the voice
      </Link>
    );
  if (type === "question")
    return (
      <Link to={`/lab/questions/${id}`} className="text-sm text-blue-700 hover:underline">
        Open the question
      </Link>
    );
  return (
    <span className="text-xs text-gray-400" title={id}>
      no standalone page — id {id.slice(0, 8)}…
    </span>
  );
}

function ReasonChip({ reason }: { reason: ReportReason }) {
  const label = REASON_LABELS[reason] ?? reason;
  if (reason === "self_harm_concern") return <Badge variant="red">{label}</Badge>;
  if (reason === "hate_speech" || reason === "harassment")
    return <Badge variant="yellow">{label}</Badge>;
  return <Badge variant="gray">{label}</Badge>;
}
